import React, {
  useContext, useState, useMemo, useEffect,
} from 'react';
import { Helmet } from 'react-helmet';
import { RouterProvider } from 'react-router-dom';
import { AppContext } from '@edx/frontend-platform/react';
import { setBasePath } from '@shoelace-style/shoelace/dist/utilities/base-path';
import { getConfig } from '@edx/frontend-platform';
import { getLocale } from '@edx/frontend-platform/i18n';

import routes from './routes';
import { type AppDataContextType, AppDataContext } from './context';
import type { AuthenticatedUser } from './types';
import { useLearningPathConfig, useAllowedCourses } from './hooks';

import '@shoelace-style/shoelace/dist/themes/light.css';

setBasePath('https://cdn.jsdelivr.net/npm/@shoelace-style/shoelace@2.18.0/cdn/');

const App = () => {
  const [appData, setAppData] = useState<AppDataContextType>({});
  const {
    authenticatedUser: platformAuthenticatedUser,
  } = useContext<{ authenticatedUser: AuthenticatedUser | null }>(AppContext);
  const [authenticatedUser, setAuthenticatedUser] = useState<AuthenticatedUser | null>(platformAuthenticatedUser);

  const { config: learningPathConfig, loaded: allowedCoursesLoaded } = useLearningPathConfig();
  const allowedCourses = useAllowedCourses(learningPathConfig);

  useEffect(() => {
    setAuthenticatedUser(platformAuthenticatedUser);
  }, [platformAuthenticatedUser]);

  const contextValue = useMemo(
    () => ({
      appData,
      authenticatedUser,
      allowedCourses,
      allowedCoursesLoaded,
      setAppData,
      setAuthenticatedUser,
    }),
    [appData, authenticatedUser, allowedCourses, allowedCoursesLoaded],
  );

  if (!platformAuthenticatedUser && localStorage.getItem('locationDataRegistered')) {
    localStorage.removeItem('locationDataRegistered');
  }

  return (
    <>
      <Helmet htmlAttributes={{ lang: getLocale() }}>
        <link rel="shortcut icon" href={getConfig().FAVICON_URL} type="image/x-icon" />
      </Helmet>
      <AppDataContext.Provider value={contextValue}>
        <RouterProvider router={routes(platformAuthenticatedUser)} />
      </AppDataContext.Provider>
    </>
  );
};

export default App;
